import React, { useState } from "react";
import { ChevronLeft, Link2, Pencil, Trash2, UserCheck } from "lucide-react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EditTeamModal from "../EditTeamModal";
import DeleteTeamModal from "../DeleteTeamModal";
import TeamInviteLinkModal from "../TeamInviteLinkModal";
import useWorkspaceRole from "v2/hooks/useWorkspaceRole";
import GuardianClaimsModal from "../GuardianClaimsModal";

const capitalizeWords = (str) =>
  str
    ?.split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "";

const TeamHeroSection = ({
  team,
  players,
  pendingClaims = 0,
  onClaimsChanged,
  onBack,
  onTeamUpdate,
  onTeamDeleted,
}) => {
  const { t } = useTranslation();
  const { isAdmin } = useWorkspaceRole();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showClaimsModal, setShowClaimsModal] = useState(false);
  const { workspaceId } = useParams();

  const handleEditTeam = (data) => {
    onTeamUpdate?.(data);
  };

  const handleDeleteTeam = () => {
    onTeamDeleted?.();
  };

  return (
    <div className="px-6 pb-4 pt-5">
      {/* Back Navigation */}
      <button
        onClick={onBack}
        className="mb-4 flex cursor-pointer items-center gap-1 text-xs font-medium text-[#6B7280] transition duration-150 hover:text-[#374151]"
      >
        <ChevronLeft size={14} />
        {t("team.hero.backToTeams")}
      </button>

      {/* Team Header */}
      <div className="flex items-start justify-between">
        {/* Left: Title + subtitle */}
        <div>
          <div className="mb-0.5 flex items-center gap-2">
            <h1 className="text-xl font-bold text-[#111827]">{team.name}</h1>
            {team.sport && (
              <span className="rounded bg-[#E0F2F1] px-1.5 py-0.5 text-[10px] font-semibold text-[#00796B]">
                {capitalizeWords(team.sport)}
              </span>
            )}
            {team.under16 && (
              <span className="rounded bg-[#EEF2FF] px-1.5 py-0.5 text-[10px] font-semibold text-[#4338CA]">
                {t("team.hero.under16")}
              </span>
            )}
          </div>
          <p className="text-xs text-[#6B7280]">
            {team.lastActive
              ? t("team.hero.athleteCountLastActive", {
                  value: team.playerCount,
                  lastActive: team.lastActive,
                })
              : t("team.hero.athleteCount", { value: team.playerCount })}
          </p>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Parent sign-ups (under-16 teams) */}
          {team.under16 && (
            <button
              onClick={() => setShowClaimsModal(true)}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm font-medium text-[#374151] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
            >
              <UserCheck size={15} className="text-[#E07A3A]" />
              {t("team.hero.signUps")}
              {pendingClaims > 0 && (
                <span className="rounded-full bg-[#E07A3A] px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                  {pendingClaims}
                </span>
              )}
            </button>
          )}

          {/* Invite link */}
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm font-medium text-[#374151] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
          >
            <Link2 size={15} className="text-[#E07A3A]" />
            {t("team.hero.inviteLink")}
          </button>

          {/* Edit Button */}
          <button
            onClick={() => setShowEditModal(true)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
            title={t("team.hero.editTeam")}
          >
            <Pencil size={15} />
          </button>

          {/* Delete Button (admins only) */}
          {isAdmin && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:border-[#FECACA] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
              title={t("team.hero.deleteTeam")}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Modals */}
      <EditTeamModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditTeam}
        team={team}
      />

      <TeamInviteLinkModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        workspaceId={workspaceId}
        team={team}
      />

      <GuardianClaimsModal
        show={showClaimsModal}
        onClose={() => setShowClaimsModal(false)}
        workspaceId={workspaceId}
        teamId={team.id}
        players={players}
        onChanged={onClaimsChanged}
      />

      <DeleteTeamModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteTeam}
        team={team}
      />
    </div>
  );
};

export default TeamHeroSection;
